/**
 * Sunrise, sunset, civil twilight and daylight remaining, computed locally.
 *
 * Computed, not fetched, so the hangar screen still knows when the day ends with the
 * internet down and no source has to be trusted for it. The method is NOAA's solar position
 * algorithm (the NOAA Global Monitoring Laboratory solar calculator, after Meeus,
 * "Astronomical Algorithms"): accurate to about a minute between ±72° latitude, which is
 * the precision anyone plans a last load to. Tested against US Naval Observatory values.
 *
 * - **Sunrise / sunset** are when the sun's upper limb meets the horizon with standard
 *   refraction: zenith 90.833°.
 * - **Civil twilight** begins/ends when the sun's centre is 6° below the horizon: zenith 96°.
 *   It is reported because it is when a jumper can still see the ground without lights.
 *
 * **Which day.** `sunTimes(lat, lon, '2026-06-21')` returns the solar day whose local solar
 * noon falls on that date, i.e. the site's own day. For a western-hemisphere site the
 * sunset is on the *next* UTC date (Denver's 21 June sunset is 02:31Z on 22 June); all
 * results are UTC instants, so that is handled without any timezone work here. Pass the
 * site-local calendar date, not today's UTC date, or an evening lookup in the Americas
 * returns tomorrow.
 *
 * **Polar day and night.** When the sun never crosses a threshold that day the time is
 * `null` with a reason, never a made-up time. The two pairs are independent: on 21 December
 * at Utqiaġvik, Alaska the sun never rises (`polar_night`) but civil twilight still comes
 * and goes.
 */

import type { UtcMs } from './types.js'

/** Zenith angle (degrees) at sunrise/sunset: 90° + 34′ refraction + 16′ solar semi-diameter. */
export const ZENITH_SUNRISE_DEG = 90.833
/** Zenith angle (degrees) at the start/end of civil twilight: sun's centre 6° below horizon. */
export const ZENITH_CIVIL_DEG = 96

const MS_PER_DAY = 86_400_000
const MS_PER_MIN = 60_000

const rad = (d: number): number => (d * Math.PI) / 180
const deg = (r: number): number => (r * 180) / Math.PI

/** Julian centuries since J2000.0 for a Julian Day. */
function julianCentury(jd: number): number {
  return (jd - 2451545) / 36525
}

interface SolarState {
  /** Declination, degrees. */
  declinationDeg: number
  /** Equation of time, minutes. */
  eqTimeMin: number
}

/** NOAA solar position: declination and equation of time at Julian century `t`. */
function solarState(t: number): SolarState {
  const l0 = (((280.46646 + t * (36000.76983 + t * 0.0003032)) % 360) + 360) % 360
  const m = 357.52911 + t * (35999.05029 - 0.0001537 * t)
  const e = 0.016708634 - t * (0.000042037 + 0.0000001267 * t)
  const mr = rad(m)
  const c =
    Math.sin(mr) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(2 * mr) * (0.019993 - 0.000101 * t) +
    Math.sin(3 * mr) * 0.000289
  const trueLong = l0 + c
  const omega = 125.04 - 1934.136 * t
  const lambda = trueLong - 0.00569 - 0.00478 * Math.sin(rad(omega))
  const seconds = 21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))
  const e0 = 23 + (26 + seconds / 60) / 60
  const obliq = e0 + 0.00256 * Math.cos(rad(omega))
  const declinationDeg = deg(Math.asin(Math.sin(rad(obliq)) * Math.sin(rad(lambda))))

  const y = Math.tan(rad(obliq) / 2) ** 2
  const l0r = rad(l0)
  const eqTime =
    y * Math.sin(2 * l0r) -
    2 * e * Math.sin(mr) +
    4 * e * y * Math.sin(mr) * Math.cos(2 * l0r) -
    0.5 * y * y * Math.sin(4 * l0r) -
    1.25 * e * e * Math.sin(2 * mr)
  return { declinationDeg, eqTimeMin: 4 * deg(eqTime) }
}

type HourAngle = { ok: true; deg: number } | { ok: false; always: 'above' | 'below' }

/** Hour angle at which the sun reaches `zenithDeg`, or which side it stays on all day. */
function hourAngle(latDeg: number, declinationDeg: number, zenithDeg: number): HourAngle {
  const latR = rad(latDeg)
  const decR = rad(declinationDeg)
  const arg = Math.cos(rad(zenithDeg)) / (Math.cos(latR) * Math.cos(decR)) - Math.tan(latR) * Math.tan(decR)
  if (arg > 1) return { ok: false, always: 'below' }
  if (arg < -1) return { ok: false, always: 'above' }
  return { ok: true, deg: deg(Math.acos(arg)) }
}

/** UTC minutes after `jd0` (0h UTC of the date) of local solar noon, refined once. */
function solarNoonMin(jd0: number, lonDeg: number): number {
  const first = 720 - 4 * lonDeg - solarState(julianCentury(jd0 + (720 - 4 * lonDeg) / 1440)).eqTimeMin
  return 720 - 4 * lonDeg - solarState(julianCentury(jd0 + first / 1440)).eqTimeMin
}

type EventResult = { ok: true; min: number } | { ok: false; always: 'above' | 'below' }

/**
 * UTC minutes after `jd0` of a rising (`rising`) or setting threshold crossing.
 * First estimate uses the sun's position at solar noon; then it is recomputed once with the
 * sun's position at the estimated time, as the NOAA calculator does.
 */
function eventMin(jd0: number, latDeg: number, lonDeg: number, zenithDeg: number, rising: boolean): EventResult {
  const noon = solarNoonMin(jd0, lonDeg)
  const atNoon = solarState(julianCentury(jd0 + noon / 1440))
  const ha0 = hourAngle(latDeg, atNoon.declinationDeg, zenithDeg)
  if (!ha0.ok) return ha0
  const estimate = (s: SolarState, ha: number): number =>
    720 - 4 * (lonDeg + (rising ? ha : -ha)) - s.eqTimeMin
  const first = estimate(atNoon, ha0.deg)
  const s1 = solarState(julianCentury(jd0 + first / 1440))
  const ha1 = hourAngle(latDeg, s1.declinationDeg, zenithDeg)
  // Within minutes of polar day/night the refined position can tip just past the limit;
  // the first estimate is then the better answer, not "no event".
  if (!ha1.ok) return { ok: true, min: first }
  return { ok: true, min: estimate(s1, ha1.deg) }
}

export type SunriseSunsetNullReason = 'polar_day' | 'polar_night'
export type CivilTwilightNullReason = 'never_darker_than_civil' | 'never_lighter_than_civil'

export interface SunTimes {
  /** The site-local calendar date these times are for, `YYYY-MM-DD`. */
  date: string
  solarNoon: UtcMs
  sunrise: UtcMs | null
  sunset: UtcMs | null
  /**
   * Why sunrise/sunset are `null`: `polar_day` (the sun never sets) or `polar_night`
   * (it never rises). `null` when both times exist.
   */
  sunriseSunsetNullReason: SunriseSunsetNullReason | null
  /** Start of morning civil twilight (sun 6° below the horizon, rising). */
  civilDawn: UtcMs | null
  /** End of evening civil twilight (sun 6° below the horizon, setting). */
  civilDusk: UtcMs | null
  /**
   * Why civil dawn/dusk are `null`: the sun stays above 6° below the horizon all day
   * (`never_darker_than_civil`, e.g. summer at high latitude) or below it all day
   * (`never_lighter_than_civil`, deep polar night).
   */
  civilTwilightNullReason: CivilTwilightNullReason | null
}

/** Parse `YYYY-MM-DD` or take the UTC calendar date of an instant; returns 0h UTC ms. */
function dayStartUtc(date: string | UtcMs): number {
  if (typeof date === 'number') {
    const d = new Date(date)
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!m) throw new RangeError(`sunTimes: date must be YYYY-MM-DD, got "${date}"`)
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

/**
 * Sun times for a site and its local calendar date.
 *
 * @param latDeg latitude, degrees north positive
 * @param lonDeg longitude, degrees **east positive** (Denver is −104.99)
 * @param date the site-local calendar date as `YYYY-MM-DD`, or an instant whose UTC calendar
 *   date is used (pass `Date.UTC(y, m - 1, d)`)
 */
export function sunTimes(latDeg: number, lonDeg: number, date: string | UtcMs): SunTimes {
  if (!Number.isFinite(latDeg) || latDeg < -90 || latDeg > 90) throw new RangeError('sunTimes: latitude out of range')
  if (!Number.isFinite(lonDeg) || lonDeg < -180 || lonDeg > 180) throw new RangeError('sunTimes: longitude out of range')
  const start = dayStartUtc(date)
  const jd0 = start / MS_PER_DAY + 2440587.5
  const at = (min: number): UtcMs => Math.round(start + min * MS_PER_MIN)

  const rise = eventMin(jd0, latDeg, lonDeg, ZENITH_SUNRISE_DEG, true)
  const set = eventMin(jd0, latDeg, lonDeg, ZENITH_SUNRISE_DEG, false)
  const dawn = eventMin(jd0, latDeg, lonDeg, ZENITH_CIVIL_DEG, true)
  const dusk = eventMin(jd0, latDeg, lonDeg, ZENITH_CIVIL_DEG, false)

  // Both crossings of one threshold are decided by the same noon-position test, so they
  // succeed or fail together; the rising result carries the reason.
  const sunriseSunsetNullReason: SunriseSunsetNullReason | null = rise.ok
    ? null
    : rise.always === 'above' ? 'polar_day' : 'polar_night'
  const civilTwilightNullReason: CivilTwilightNullReason | null = dawn.ok
    ? null
    : dawn.always === 'above' ? 'never_darker_than_civil' : 'never_lighter_than_civil'

  return {
    date: new Date(start).toISOString().slice(0, 10),
    solarNoon: at(solarNoonMin(jd0, lonDeg)),
    sunrise: rise.ok && set.ok ? at(rise.min) : null,
    sunset: rise.ok && set.ok ? at(set.min) : null,
    sunriseSunsetNullReason,
    civilDawn: dawn.ok && dusk.ok ? at(dawn.min) : null,
    civilDusk: dawn.ok && dusk.ok ? at(dusk.min) : null,
    civilTwilightNullReason,
  }
}

export interface DaylightRemaining {
  /**
   * Seconds of daylight (sunrise to sunset) still to come in this solar day: before sunrise
   * the whole day's length, during the day the time until sunset, after sunset 0.
   * `null` in polar day (the sun does not set, so there is no end to count to).
   */
  remainingS: number | null
  /** Seconds until the end of civil twilight; 0 once past; `null` when there is none. */
  untilCivilDuskS: number | null
  /** Is the sun up at `now`? */
  sunUp: boolean
  reason: SunriseSunsetNullReason | null
}

/** Daylight remaining at `now` for a day computed by `sunTimes`. */
export function daylightRemaining(times: SunTimes, now: UtcMs): DaylightRemaining {
  const untilCivilDuskS = times.civilDusk === null ? null : Math.max(0, (times.civilDusk - now) / 1000)
  if (times.sunriseSunsetNullReason === 'polar_day') {
    return { remainingS: null, untilCivilDuskS, sunUp: true, reason: 'polar_day' }
  }
  if (times.sunriseSunsetNullReason === 'polar_night' || times.sunrise === null || times.sunset === null) {
    return { remainingS: 0, untilCivilDuskS, sunUp: false, reason: 'polar_night' }
  }
  const from = Math.max(now, times.sunrise)
  return {
    remainingS: Math.max(0, (times.sunset - from) / 1000),
    untilCivilDuskS,
    sunUp: now >= times.sunrise && now < times.sunset,
    reason: null,
  }
}
